import { FC, useMemo } from 'react';
import { useTodayRosterPositions } from './useTodayRoster';
import { ReadEmployee, useGetEmployeesByLocationsQuery } from '@/services/gql/graphql.generated';
import { useLocationService } from '@/services/location/LocationService';
import { secondsToTimeString } from '@/helpers/dateHelper';
import { EmployeeAvatar } from '../clockIn/EmployeeAvatar';
import { constructNameEN } from '@/employee/employee-name-helper';

interface EmployeeWithShiftTimes {
  employee: ReadEmployee;
  shiftTimes: string;
}

const CurrentlyRosteredEmployee: FC<{ employeeWithShift: EmployeeWithShiftTimes }> = ({ employeeWithShift }) => {
  return (
    <div key={employeeWithShift.employee.identityId} className="flex flex-col items-center">
      <EmployeeAvatar
        employee={{
          firstName: employeeWithShift.employee.firstName ?? '',
          lastName: employeeWithShift.employee.lastName ?? '',
          avatarUrl: employeeWithShift.employee.avatarUrl ?? undefined,
          id: employeeWithShift.employee.identityId
        }}
      />
      <p className="font-semibold">
        {constructNameEN(employeeWithShift.employee.firstName, employeeWithShift.employee.lastName || '')}
      </p>
      <p>{employeeWithShift.shiftTimes}</p>
    </div>
  );
};

export const CurrentlyRostered: FC = () => {
  const rosterPositions = useTodayRosterPositions();

  const selectedLocation = useLocationService(s => s.selectedLocation);

  if (!selectedLocation) {
    throw Error('Location must be selected');
  }
  const [{ data: employeeData }] = useGetEmployeesByLocationsQuery({
    variables: { locationIds: selectedLocation.id }
  });

  const shiftsToDisplay: EmployeeWithShiftTimes[] | undefined = useMemo(() => {
    const positions = rosterPositions
      ?.filter(p => p.employeeId)
      .map(p => {
        const employee = employeeData?.employeesByLocations?.employees?.find(e => e.identityId === p.employeeId);

        if (!employee) {
          throw Error('Unknown employee detected');
        }

        return {
          employee: employee as ReadEmployee,
          shiftTimes: `${secondsToTimeString(p.start!)} - ${secondsToTimeString(p.end!)}`
        };
      });

    positions?.sort((a, b) => a.shiftTimes.localeCompare(b.shiftTimes));

    return positions;
  }, [rosterPositions, employeeData]);

  return (
    <div className="px-4 py-5 space-y-5">
      <p className="font-semibold">Currently Rostered</p>
      <div className="grid grid-cols-4 gap-5">
        {shiftsToDisplay?.map(s => (
          <CurrentlyRosteredEmployee employeeWithShift={s} key={s.employee.identityId} />
        ))}
      </div>
    </div>
  );
};
